/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Account Cashback History Table component
 *
 * @author Yakov Gushchin
 * @version 1.14.0
 * @since 1.12.0
 */

import { useEffect } from 'react';

import { Table } from '../../../../components/common';
import { useTable } from '../../../../components/Table/hooks/useTable';
import { ITableField } from '../../../../components/Table/reducers/tableReducer';
import TableColumnUtil, { ITableColumnUtilProps } from '../../../../components/Table/TableColumnUtil';
import { MapTableNameToApiPath, MapTableNameToListApiPath } from '../../../../components/Table/types/tableMapTypes';
import { TableNameType } from '../../../../components/Table/types/tableTypes';
import { ACCOUNT_CASHBACK_HISTORY, ACCOUNT_CASHBACK_HISTORY_LIST } from '../../../../modules/app/Api';
import { ICommonTranslation, ICorporateTranslation } from '../../../../modules/internationalization/types';
import { IPageCashbackHistoryTranslation } from '../../../../modules/internationalization/types/pages/cashback';
import { stringFormatParams } from '../../../../utils/stringFormat';
import { IPageCashbackReducer } from '../../pageCashbackReducer';
import { CashbackHistoryTableField } from '../../types/cashbackHistoryTypes';
import {
    CashbackTableCellConfig,
    CashbackTableNames,
    getCashbackTableColumnCellType,
    getCashbackTableColumnFilterType,
} from './CashbackHistoryTableCells';

type TCashbackTranslation = ICommonTranslation & ICorporateTranslation & IPageCashbackHistoryTranslation;

export interface ICashbackHistoryTableProps extends Pick<IPageCashbackReducer, 'accountSelectCurrent'> {
    translation: TCashbackTranslation;
}

type ITableColumnUtil = Pick<ITableColumnUtilProps<CashbackHistoryTableField,
        TCashbackTranslation>, 'componentsFeature' | 'fields' | 'isTableResizeEnabled'>
    & { translation: TCashbackTranslation };

const tableColumnUtil = (props: ITableColumnUtil) =>
    new TableColumnUtil<CashbackHistoryTableField, TCashbackTranslation>({
        cellConfig: CashbackTableCellConfig,
        columnNames: CashbackTableNames,
        componentsFeature: props.componentsFeature,
        fields: props.fields as Array<ITableField<CashbackHistoryTableField>>,
        getColumnCellType: getCashbackTableColumnCellType,
        getColumnFilterType: getCashbackTableColumnFilterType,
        getColumnFixedType: () => false,
        isTableResizeEnabled: props.isTableResizeEnabled,
        translation: props.translation,
    });

const CashbackHistoryTable = (props: ICashbackHistoryTableProps) => {

    const {
        accountSelectCurrent,
        translation,
    } = props;

    // get table props
    const {
        componentsFeature,
        count,
        data,
        fields,
        isLoading,
        isTableResizeEnabled,
        sort,

        loadTableMeta,
        loadTableList,
        saveResizedChange,
        setTableSort,
    } = useTable(TableNameType.cashbackHistory);

    useEffect(() => {
        MapTableNameToApiPath[TableNameType.cashbackHistory] =
            stringFormatParams(ACCOUNT_CASHBACK_HISTORY, { clientId: accountSelectCurrent.value });
        MapTableNameToListApiPath[TableNameType.cashbackHistory] =
            stringFormatParams(ACCOUNT_CASHBACK_HISTORY_LIST, { clientId: accountSelectCurrent.value });
        loadTableMeta();
    }, [accountSelectCurrent.value, loadTableMeta]);


    return (
        <Table
            columns={tableColumnUtil({
                componentsFeature,
                fields: fields as Array<ITableField<CashbackHistoryTableField>>,
                isTableResizeEnabled,
                translation,
            }).customizeTableColumns()}
            counter={count}
            data={data}
            fields={fields}
            isLoading={isLoading}
            loadTableList={loadTableList}
            saveResizedChange={saveResizedChange}
            setTableSort={setTableSort}
            showCheckedColumn={false}
            sort={sort}
            tableDataName="cashbackHistory"
            tableName={TableNameType.cashbackHistory}
        />
    );
};

export default CashbackHistoryTable;
